/**
 * Base URL for the backend API.
 * In development, CRA proxy forwards /api requests to http://localhost:3001.
 * The empty string here lets requests go through the proxy.
 * For production builds, set REACT_APP_API_BASE_URL via environment variable.
 */
export const API_BASE_URL: string =
  process.env.REACT_APP_API_BASE_URL || '';

/**
 * WebSocket URL for agent chat (served by the API server).
 * In development, connects directly to localhost:5001.
 * For production builds, set REACT_APP_WS_URL via environment variable.
 */
export const WS_URL: string =
  process.env.REACT_APP_WS_URL || 'ws://localhost:5001/ws';

/**
 * Default perspective to load when no selection has been made.
 */
export const DEFAULT_PERSPECTIVE: string = 'core_metrics';

/**
 * Supported SQL aggregate functions for metric columns.
 */
export const AGGREGATE_FUNCTIONS: { value: string; label: string }[] = [
  { value: 'SUM', label: 'Sum' },
  { value: 'AVG', label: 'Average' },
  { value: 'COUNT', label: 'Count' },
  { value: 'COUNT_DISTINCT', label: 'Count Distinct' },
  { value: 'MIN', label: 'Minimum' },
  { value: 'MAX', label: 'Maximum' },
  { value: 'MEDIAN', label: 'Median' },
  { value: 'STDDEV', label: 'Std Deviation' },
  { value: 'VARIANCE', label: 'Variance' },
];

/**
 * Predefined time windows for temporal filtering.
 */
export const TIME_WINDOWS: { value: string; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_14_days', label: 'Last 14 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Time period aggregation options for per-metric time-based CASE WHEN wrapping.
 */
export const TIME_PERIOD_AGGREGATIONS: { value: string; label: string; group: 'period' | 'comparison' }[] = [
  { value: 'WTD',       label: 'Week to Date',          group: 'period' },
  { value: 'MTD',       label: 'Month to Date',         group: 'period' },
  { value: 'QTD',       label: 'Quarter to Date',       group: 'period' },
  { value: 'YTD',       label: 'Year to Date',          group: 'period' },
  { value: 'PRIOR_1D',  label: 'Prior Day',             group: 'period' },
  { value: 'PRIOR_1M',  label: 'Prior Month',           group: 'period' },
  { value: 'PRIOR_1Y',  label: 'Prior Year',            group: 'period' },
  { value: 'MoM',               label: 'Month over Month',      group: 'comparison' },
  { value: 'QoQ',               label: 'Quarter over Quarter',  group: 'comparison' },
  { value: 'ROLLING_7D',        label: 'Rolling 7 Days',        group: 'period' },
  { value: 'ROLLING_30D',       label: 'Rolling 30 Days',       group: 'period' },
  { value: 'ROLLING_90D',       label: 'Rolling 90 Days',       group: 'period' },
  { value: 'TRAILING_12M',      label: 'Trailing 12 Months',    group: 'period' },
  { value: 'TRAILING_4Q',       label: 'Trailing 4 Quarters',   group: 'period' },
  { value: 'PRIOR_YEAR_PERIOD', label: 'Prior Year Period',     group: 'period' },
  { value: 'YoY',               label: 'Year over Year',        group: 'comparison' },
];

/**
 * Supported filter operators for dimension filtering.
 */
export const FILTER_OPERATORS: { value: string; label: string }[] = [
  { value: '=', label: 'Equals' },
  { value: '!=', label: 'Not Equals' },
  { value: '>', label: 'Greater Than' },
  { value: '>=', label: 'Greater Than or Equal' },
  { value: '<', label: 'Less Than' },
  { value: '<=', label: 'Less Than or Equal' },
  { value: 'LIKE', label: 'Like' },
  { value: 'NOT LIKE', label: 'Not Like' },
  { value: 'IN', label: 'In' },
  { value: 'NOT IN', label: 'Not In' },
  { value: 'IS NULL', label: 'Is Null' },
  { value: 'IS NOT NULL', label: 'Is Not Null' },
  { value: 'BETWEEN', label: 'Between' },
];

/**
 * Filter operators organized by dimension data type.
 */
export const OPERATORS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  categorical: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not Equals' },
    { value: 'IN', label: 'In' },
    { value: 'NOT IN', label: 'Not In' },
    { value: 'LIKE', label: 'Like' },
    { value: 'NOT LIKE', label: 'Not Like' },
    { value: 'IS NULL', label: 'Is Null' },
    { value: 'IS NOT NULL', label: 'Is Not Null' },
  ],
  date: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not Equals' },
    { value: '>', label: 'After' },
    { value: '>=', label: 'On or After' },
    { value: '<', label: 'Before' },
    { value: '<=', label: 'On or Before' },
    { value: 'BETWEEN', label: 'Between' },
    { value: 'IS NULL', label: 'Is Null' },
    { value: 'IS NOT NULL', label: 'Is Not Null' },
  ],
  number: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not Equals' },
    { value: '>', label: 'Greater Than' },
    { value: '>=', label: 'Greater Than or Equal' },
    { value: '<', label: 'Less Than' },
    { value: '<=', label: 'Less Than or Equal' },
    { value: 'BETWEEN', label: 'Between' },
    { value: 'IN', label: 'In' },
    { value: 'IS NULL', label: 'Is Null' },
    { value: 'IS NOT NULL', label: 'Is Not Null' },
  ],
  boolean: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not Equals' },
    { value: 'IS NULL', label: 'Is Null' },
    { value: 'IS NOT NULL', label: 'Is Not Null' },
  ],
  json: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not Equals' },
    { value: 'IN', label: 'In' },
    { value: 'NOT IN', label: 'Not In' },
    { value: 'LIKE', label: 'Like' },
    { value: 'NOT LIKE', label: 'Not Like' },
    { value: 'IS NULL', label: 'Is Null' },
    { value: 'IS NOT NULL', label: 'Is Not Null' },
  ],
};

// ============================================================
// Feedback Tags
// ============================================================

export const POSITIVE_FEEDBACK_TAGS: string[] = [
  'Accurate data',
  'Relevant metrics',
  'Good analysis',
  'Correct SQL',
  'Helpful visualization',
];

export const NEGATIVE_FEEDBACK_TAGS: string[] = [
  'Wrong metrics/dimensions',
  'Inaccurate data',
  'Irrelevant answer',
  'Bad SQL',
  'Missing context',
];

export const ROW_LIMIT_OPTIONS: number[] = [500, 1000, 5000, 10000, 50000, 100000];
export const ROWS_PER_PAGE_OPTIONS: number[] = [25, 50, 100, 500, 1000];
export const DEFAULT_USER_SETTINGS: { resultLimit: number; modelSource: 'production' | 'draft' } = {
  resultLimit: 1000,
  modelSource: 'production',
};

export interface TrinoFunctionDef {
  name: string;
  category: 'string' | 'date' | 'math' | 'conditional' | 'aggregate' | 'json';
  signature: string;
  description: string;
  applicableTypes: string[];
  insertMode: 'no_arg' | 'single_arg' | 'template';
  template: string;
}

export const TRINO_FUNCTIONS: TrinoFunctionDef[] = [
  // String
  { name: 'UPPER',     category: 'string', signature: 'UPPER(value)',                    description: 'Convert to uppercase',   applicableTypes: ['categorical'], insertMode: 'single_arg', template: 'UPPER(' },
  { name: 'LOWER',     category: 'string', signature: 'LOWER(value)',                    description: 'Convert to lowercase',   applicableTypes: ['categorical'], insertMode: 'single_arg', template: 'LOWER(' },
  { name: 'TRIM',      category: 'string', signature: 'TRIM(value)',                     description: 'Remove whitespace',      applicableTypes: ['categorical'], insertMode: 'single_arg', template: 'TRIM(' },
  { name: 'LENGTH',    category: 'string', signature: 'LENGTH(value)',                   description: 'String length',          applicableTypes: ['categorical'], insertMode: 'single_arg', template: 'LENGTH(' },
  { name: 'SUBSTRING', category: 'string', signature: 'SUBSTRING(value, start, length)', description: 'Extract substring',      applicableTypes: ['categorical'], insertMode: 'template',   template: 'SUBSTRING(, 1, 5)' },
  { name: 'REPLACE',   category: 'string', signature: "REPLACE(value, search, replace)", description: 'Replace in string',      applicableTypes: ['categorical'], insertMode: 'template',   template: "REPLACE(, '', '')" },
  { name: 'CONCAT',    category: 'string', signature: 'CONCAT(val1, val2, ...)',         description: 'Concatenate strings',    applicableTypes: ['categorical'], insertMode: 'template',   template: "CONCAT(, '')" },
  // Date
  { name: 'DATE_TRUNC',        category: 'date', signature: "DATE_TRUNC('unit', date)",  description: 'Truncate date to unit',    applicableTypes: ['date'], insertMode: 'template', template: "DATE_TRUNC('month', )" },
  { name: 'DATE_ADD',          category: 'date', signature: "DATE_ADD('unit', n, date)",  description: 'Add interval to date',     applicableTypes: ['date'], insertMode: 'template', template: "DATE_ADD('day', 1, )" },
  { name: 'DATE_DIFF',         category: 'date', signature: "DATE_DIFF('unit', d1, d2)", description: 'Difference between dates', applicableTypes: ['date'], insertMode: 'template', template: "DATE_DIFF('day', , )" },
  { name: 'CURRENT_DATE',      category: 'date', signature: 'CURRENT_DATE',              description: 'Current date',             applicableTypes: ['date'], insertMode: 'no_arg',   template: 'CURRENT_DATE' },
  { name: 'CURRENT_TIMESTAMP', category: 'date', signature: 'CURRENT_TIMESTAMP',         description: 'Current timestamp',        applicableTypes: ['date'], insertMode: 'no_arg',   template: 'CURRENT_TIMESTAMP' },
  // Math
  { name: 'ROUND', category: 'math', signature: 'ROUND(value, decimals)', description: 'Round to decimals', applicableTypes: ['number', 'currency', 'percentage', 'integer'], insertMode: 'template',   template: 'ROUND(, 2)' },
  { name: 'ABS',   category: 'math', signature: 'ABS(value)',              description: 'Absolute value',   applicableTypes: ['number', 'currency', 'percentage', 'integer'], insertMode: 'single_arg', template: 'ABS(' },
  { name: 'CEIL',  category: 'math', signature: 'CEIL(value)',             description: 'Round up',         applicableTypes: ['number', 'currency', 'percentage', 'integer'], insertMode: 'single_arg', template: 'CEIL(' },
  { name: 'FLOOR', category: 'math', signature: 'FLOOR(value)',            description: 'Round down',       applicableTypes: ['number', 'currency', 'percentage', 'integer'], insertMode: 'single_arg', template: 'FLOOR(' },
  { name: 'MOD',   category: 'math', signature: 'MOD(value, divisor)',     description: 'Remainder',        applicableTypes: ['number', 'integer'],                           insertMode: 'template',   template: 'MOD(, 2)' },
  // Conditional
  { name: 'COALESCE', category: 'conditional', signature: 'COALESCE(val1, val2, ...)', description: 'First non-null value',   applicableTypes: ['categorical', 'date', 'number', 'currency', 'percentage', 'integer', 'boolean'], insertMode: 'template', template: "COALESCE(, '')" },
  { name: 'NULLIF',   category: 'conditional', signature: 'NULLIF(val1, val2)',         description: 'Null if values equal',   applicableTypes: ['categorical', 'date', 'number', 'currency', 'percentage', 'integer', 'boolean'], insertMode: 'template', template: 'NULLIF(, 0)' },
  { name: 'IF',       category: 'conditional', signature: 'IF(cond, true, false)',      description: 'Conditional expression', applicableTypes: ['categorical', 'date', 'number', 'currency', 'percentage', 'integer', 'boolean'], insertMode: 'template', template: 'IF( > 0, , 0)' },
  // Aggregate
  { name: 'SUM',            category: 'aggregate', signature: 'SUM(column)',               description: 'Sum of values',           applicableTypes: ['number', 'currency', 'percentage', 'integer'], insertMode: 'single_arg', template: 'SUM(' },
  { name: 'AVG',            category: 'aggregate', signature: 'AVG(column)',               description: 'Average of values',       applicableTypes: ['number', 'currency', 'percentage', 'integer'], insertMode: 'single_arg', template: 'AVG(' },
  { name: 'COUNT',          category: 'aggregate', signature: 'COUNT(column)',             description: 'Count of rows',           applicableTypes: ['categorical', 'date', 'number', 'currency', 'percentage', 'integer', 'boolean'], insertMode: 'single_arg', template: 'COUNT(' },
  { name: 'COUNT_DISTINCT', category: 'aggregate', signature: 'COUNT(DISTINCT column)',    description: 'Count distinct values',   applicableTypes: ['categorical', 'date', 'number', 'currency', 'percentage', 'integer', 'boolean'], insertMode: 'single_arg', template: 'COUNT(DISTINCT ' },
  { name: 'MIN',            category: 'aggregate', signature: 'MIN(column)',               description: 'Minimum value',           applicableTypes: ['number', 'currency', 'percentage', 'integer', 'date'], insertMode: 'single_arg', template: 'MIN(' },
  { name: 'MAX',            category: 'aggregate', signature: 'MAX(column)',               description: 'Maximum value',           applicableTypes: ['number', 'currency', 'percentage', 'integer', 'date'], insertMode: 'single_arg', template: 'MAX(' },
  // JSON
  { name: 'JSON_EXTRACT_SCALAR', category: 'json', signature: "JSON_EXTRACT_SCALAR(json, '$.path')", description: 'Extract scalar value as VARCHAR', applicableTypes: ['json', 'categorical'], insertMode: 'template', template: "JSON_EXTRACT_SCALAR(, '$.key')" },
  { name: 'JSON_EXTRACT',        category: 'json', signature: "JSON_EXTRACT(json, '$.path')",        description: 'Extract JSON element',            applicableTypes: ['json'],                insertMode: 'template', template: "JSON_EXTRACT(, '$.key')" },
  { name: 'JSON_ARRAY_LENGTH',   category: 'json', signature: 'JSON_ARRAY_LENGTH(json_array)',       description: 'Length of JSON array',             applicableTypes: ['json'],                insertMode: 'single_arg', template: 'JSON_ARRAY_LENGTH(' },
  { name: 'JSON_ARRAY_GET',      category: 'json', signature: 'JSON_ARRAY_GET(json_array, index)',   description: 'Get element from JSON array',      applicableTypes: ['json'],                insertMode: 'template', template: 'JSON_ARRAY_GET(, 0)' },
  { name: 'JSON_FORMAT',         category: 'json', signature: 'JSON_FORMAT(json)',                   description: 'Serialize JSON to VARCHAR',        applicableTypes: ['json'],                insertMode: 'single_arg', template: 'JSON_FORMAT(' },
  { name: 'JSON_PARSE',          category: 'json', signature: 'JSON_PARSE(string)',                  description: 'Parse VARCHAR to JSON',            applicableTypes: ['categorical', 'json'], insertMode: 'single_arg', template: 'JSON_PARSE(' },
  { name: 'JSON_SIZE',           category: 'json', signature: "JSON_SIZE(json, '$.path')",           description: 'Size of JSON array or object',     applicableTypes: ['json'],                insertMode: 'template', template: "JSON_SIZE(, '$.key')" },
  { name: 'CAST_JSON_TO_INT',    category: 'json', signature: "CAST(JSON_EXTRACT_SCALAR(...) AS BIGINT)", description: 'Extract and cast to integer', applicableTypes: ['json'], insertMode: 'template', template: "CAST(JSON_EXTRACT_SCALAR(, '$.key') AS BIGINT)" },
  { name: 'CAST_JSON_TO_DOUBLE', category: 'json', signature: "CAST(JSON_EXTRACT_SCALAR(...) AS DOUBLE)", description: 'Extract and cast to double', applicableTypes: ['json'], insertMode: 'template', template: "CAST(JSON_EXTRACT_SCALAR(, '$.key') AS DOUBLE)" },
];

export const TRINO_FUNCTION_CATEGORIES: { key: string; label: string }[] = [
  { key: 'aggregate', label: 'Aggregate' },
  { key: 'math', label: 'Math' },
  { key: 'string', label: 'String' },
  { key: 'date', label: 'Date' },
  { key: 'conditional', label: 'Conditional' },
  { key: 'json', label: 'JSON' },
];
